import AltRouteIcon from '@mui/icons-material/AltRoute';
import {
  PAGINATION,
  ROUTE_STATUSES,
  createRouteSchema,
  formatDate,
  type CreateRouteInput,
  type Route,
} from '@fdt/shared';
import { Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CrudPage } from '../components/CrudPage';
import type { Column } from '../components/DataTable';
import { currentLocale } from '../i18n';
import { usersApi } from '../resources';
import { routesApi } from '../resources/routes';

interface FormState {
  routeDate: string;
  name: string;
  assignedUserId: string;
  notes: string;
}

const STATUS_COLOUR: Record<Route['status'], 'default' | 'info' | 'warning' | 'success'> = {
  draft: 'default',
  assigned: 'info',
  started: 'warning',
  completed: 'success',
};

/** `YYYY-MM-DD` for today in Europe/Rome — the same business day the API uses. */
function todayInRome(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function RoutesPage() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const navigate = useNavigate();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [staffFilter, setStaffFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filters = useMemo(
    () => ({
      from: from || undefined,
      to: to || undefined,
      assignedUserId: staffFilter || undefined,
      status: statusFilter || undefined,
    }),
    [from, to, staffFilter, statusFilter],
  );

  const staff = useQuery({
    queryKey: ['users', 'field_staff'],
    queryFn: () =>
      usersApi.list({ page: 1, pageSize: PAGINATION.maxPageSize, role: 'field_staff' }),
  });

  const columns: Column<Route>[] = [
    {
      id: 'routeDate',
      label: t('fields.date'),
      render: (row) => formatDate(`${row.routeDate}T00:00:00Z`, locale),
    },
    { id: 'name', label: t('fields.name'), render: (row) => row.name ?? '—' },
    {
      id: 'assignedUserName',
      label: t('routes.assignee'),
      render: (row) => row.assignedUserName ?? '—',
    },
    { id: 'vehiclePlate', label: t('fields.vehicle'), render: (row) => row.vehiclePlate ?? '—' },
    {
      id: 'stops',
      label: t('routes.stops'),
      align: 'right',
      render: (row) =>
        row.status === 'draft' || row.status === 'assigned'
          ? row.stopCount
          : t('routes.visitedOf', { visited: row.visitedCount, total: row.stopCount }),
    },
    {
      id: 'planned',
      label: t('routes.planned'),
      align: 'right',
      render: (row) =>
        row.plannedDistanceMeters === null ? (
          <Chip size="small" variant="outlined" label={t('routes.notPlanned')} />
        ) : (
          <Stack spacing={0} alignItems="flex-end">
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {(row.plannedDistanceMeters / 1000).toFixed(1)} km
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {Math.round((row.plannedDurationSeconds ?? 0) / 60)} {t('routes.minutes')}
              {row.geometryStale ? ` · ${t('routes.stale')}` : ''}
            </Typography>
          </Stack>
        ),
    },
    {
      id: 'status',
      label: t('fields.status'),
      render: (row) => (
        <Chip size="small" color={STATUS_COLOUR[row.status]} label={t(`routes.statuses.${row.status}`)} />
      ),
    },
  ];

  return (
    <CrudPage<Route, FormState, CreateRouteInput, never>
      title={t('nav.routes')}
      icon={AltRouteIcon}
      tone="accent"
      resourceKey="routes"
      // `routesApi` has extra verbs, but the list screen only needs these four.
      api={routesApi as never}
      columns={columns}
      rowKey={(row) => row.id}
      emptyForm={{ routeDate: todayInRome(), name: '', assignedUserId: '', notes: '' }}
      filters={filters}
      schema={createRouteSchema}
      // Editing a route happens on its own screen, not in a dialog.
      toForm={() => ({ routeDate: todayInRome(), name: '', assignedUserId: '', notes: '' })}
      toPayload={(form) => ({
        routeDate: form.routeDate,
        name: form.name || null,
        assignedUserId: form.assignedUserId || null,
        notes: form.notes || null,
      })}
      createTitle={t('routes.createTitle')}
      editTitle={t('routes.createTitle')}
      deleteTitle={t('routes.deleteTitle')}
      deleteMessage={(row) =>
        t('routes.deleteMessage', {
          date: formatDate(`${row.routeDate}T00:00:00Z`, locale),
          name: row.name ?? '',
        })
      }
      // The whole row opens the editor: as one small chip among three it was
      // easy to miss and easy to mistake for the status badge next to it.
      onRowClick={(row) => navigate(`/routes/${row.id}`)}
      toolbar={
        <>
          <TextField
            label={t('routes.from')}
            type="date"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 170 }}
          />
          <TextField
            label={t('routes.to')}
            type="date"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ width: 170 }}
          />
          <TextField
            select
            label={t('routes.assignee')}
            value={staffFilter}
            onChange={(event) => setStaffFilter(event.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(staff.data?.items ?? []).map((person) => (
              <MenuItem key={person.id} value={person.id}>
                {person.firstName} {person.lastName}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('fields.status')}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {ROUTE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {t(`routes.statuses.${status}`)}
              </MenuItem>
            ))}
          </TextField>
        </>
      }
      renderForm={({ form, setForm, fieldErrors }) => (
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.date')}
              type="date"
              value={form.routeDate}
              onChange={(event) => setForm({ ...form, routeDate: event.target.value })}
              error={Boolean(fieldErrors.routeDate)}
              helperText={fieldErrors.routeDate ? t(fieldErrors.routeDate) : undefined}
              slotProps={{ inputLabel: { shrink: true } }}
              required
              fullWidth
            />
            <TextField
              select
              label={t('routes.assignee')}
              value={form.assignedUserId}
              onChange={(event) => setForm({ ...form, assignedUserId: event.target.value })}
              helperText={t('routes.assigneeHelp')}
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {(staff.data?.items ?? [])
                .filter((person) => person.isActive)
                .map((person) => (
                  <MenuItem key={person.id} value={person.id}>
                    {person.firstName} {person.lastName}
                  </MenuItem>
                ))}
            </TextField>
          </Stack>

          <TextField
            label={t('fields.name')}
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            helperText={t('routes.nameHelp')}
            fullWidth
          />

          <TextField
            label={t('fields.notes')}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      )}
    />
  );
}
